import React from "react";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import {
  FormHelperText,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

const DATE_RANGE_OPTIONS = ["Last 7 Days", "Last 30 Days", "Custom"];

const DateRangeToggle = ({
  value = [],
  onChange = () => {},
  label = "Date Range",
  alignRight = false,
  disableLabel = false,
  disabled = false,
}) => {
  const [dateRange, setDateRange] = React.useState(value);
  const [rangeType, setRangeType] = React.useState(DATE_RANGE_OPTIONS[0]);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const openPopover = Boolean(anchorEl);
  const popoverId = openPopover ? "simple-popover" : undefined;

  // methods
  const handlePopper = (event, type) => {
    if (type !== "Custom") return setAnchorEl(null);
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopper = () => setAnchorEl(null);

  React.useEffect(() => {
    // -1 to exclude today
    const _weekDateRange = [
      moment()
        .subtract(7 - 1, "d")
        .format("MM-DD-YYYY"),
      moment().format("MM-DD-YYYY"),
    ];

    const _monthDateRange = [
      moment()
        .subtract(30 - 1, "d")
        .format("MM-DD-YYYY"),
      moment().format("MM-DD-YYYY"),
    ];

    switch (rangeType) {
      case DATE_RANGE_OPTIONS[0]:
        setDateRange(_weekDateRange);
        break;

      case DATE_RANGE_OPTIONS[1]:
        setDateRange(_monthDateRange);
        break;

      case DATE_RANGE_OPTIONS[2]:
        setDateRange((prev) => prev ?? _weekDateRange);
        break;

      default:
        setDateRange(_weekDateRange);
    }
  }, [rangeType]);

  React.useEffect(() => {
    if (!dateRange?.length) return;

    if (dateRange?.some((date) => !date)) return;

    onChange(dateRange);
  }, [dateRange, onChange]);

  return (
    <div className={twMerge("flex flex-col", alignRight && "items-end")}>
      <div>
        {!disableLabel && (
          <div className="mb-1">
            <FormHelperText>{label}</FormHelperText>
          </div>
        )}

        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={rangeType}
          onChange={(_, val) => {
            if (val !== null) setRangeType(val);
          }}
          disabled={disabled}
        >
          {DATE_RANGE_OPTIONS.map((type) => (
            <ToggleButton
              key={type}
              value={type}
              onClick={(event) => handlePopper(event, type)}
            >
              {type}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      <Popover
        id={popoverId}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopper}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <div className="grid w-72 grid-cols-1 gap-6 p-4">
            <div>
              <DesktopDatePicker
                inputFormat={"MM/DD/YYYY"}
                value={dateRange[0]}
                onChange={(date) => setDateRange(() => [date, null])}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="From Date" />
                )}
                disableFuture
                disabled={disabled}
              />
            </div>

            <div>
              <DesktopDatePicker
                inputFormat={"MM/DD/YYYY"}
                value={dateRange[1]}
                onChange={(date) => setDateRange((prev) => [prev[0], date])}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="To Date" />
                )}
                disableFuture
                shouldDisableDate={(date) => date < dateRange[0]}
                disabled={!dateRange[0] || disabled}
              />
            </div>
          </div>
        </LocalizationProvider>
      </Popover>
    </div>
  );
};

export default DateRangeToggle;
